import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { InfoCircleOutlined } from "@ant-design/icons";
import { checkValidRelativeDateString } from "@regal-voice/shared-parsers";
import { Form, Input, Select, Popover } from "antd";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import set from "lodash/set";
import { useDispatch } from "react-redux";

import {
    RVFormFloatingLabel,
    RVFormItem,
    ConfigurableVariableOption,
    ConfigurableVariablesPickerPlugin,
    CustomVariablesHighlightPlugin,
    PlainTextEditor,
    HANDLEBAR_TRIGGERS,
    HANDLEBARS_WRAP,
} from "@regal-voice/ui";
import { JourneyFormContext } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/shared/Contexts/JourneyFormContext";
import { useQueryEventAttributes } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/shared/hooks/useQueryEventAttributes";
import { createOptionsWithPrefixWithOriginalFieldName } from "ServerData/EventSchema/Utils/eventSchemaUtils";
import { setFields } from "Services/state/journeys/JourneyStateSlice";
import { ConditionalEventType, NodeTriggerType } from "Types/Journey";

import { unflattenFields } from "../../../Utils/utils";
import { DatePickerItem } from "./DatePickerItem";
import { RealtiveDatePopover } from "./RealtiveDatePopover";

import styles from "../ConditionalFormItems.module.scss";

const HANDLEBARS_REGEX_LIST = [HANDLEBARS_WRAP];

export function ConditionalNodeDatePropertyValue({
    rootField,
    propertyValueDisabled,
    propertyValueRequired,
    dateComparisonType,
    formValues,
    triggeringEvent,
}: {
    rootField?: any;
    propertyOperator: string;
    propertyValueRequired: any;
    propertyValueDisabled: boolean;
    propertyFieldId: string;
    isContactCondtional: boolean;
    relativeDateValue?: string;
    dateComparisonType?: string;
    formValues: any;
    triggeringEvent: { eventType?: NodeTriggerType | ConditionalEventType; eventName?: string };
}): JSX.Element {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { formSubmited, form } = useContext(JourneyFormContext)!;
    const [dateTimeComparisonLocal, setDateTimeComparison] = useState<string>(dateComparisonType || "specific");

    const dispatch = useDispatch();

    /* Getting the event fields */
    const { loading, eventAttributes } = useQueryEventAttributes(triggeringEvent);
    const eventOptions: { value: string; label: string; dataType: string }[] = useMemo(() => {
        if (!loading && eventAttributes.length > 0) {
            return eventAttributes
                .filter((event) => event.fieldType === "datetime")
                .map((event) => createOptionsWithPrefixWithOriginalFieldName(event, "event"));
        }
        return [];
    }, [loading, eventAttributes]);

    const allOptions = useMemo<Array<ConfigurableVariableOption>>(
        () => eventOptions.map((option) => new ConfigurableVariableOption(option.label, option.value, option.dataType)),
        [eventOptions]
    );

    const validateRelativeDate = useCallback(
        async (_: any, relativeDateValue: string) => {
            if (!formSubmited) {
                return Promise.resolve();
            }
            if (relativeDateValue && checkValidRelativeDateString(relativeDateValue)) {
                return Promise.resolve();
            }
            return Promise.reject(new Error("Invalid relative date format"));
        },
        [formSubmited]
    );

    const validateSpecificDate = useCallback(
        async (_: any, date: string) => {
            if (!formSubmited) {
                return Promise.resolve();
            }
            if (dateComparisonType == "specific" && !date) {
                return Promise.reject(new Error("Invalid date"));
            }
            return Promise.resolve();
        },
        [formSubmited, dateComparisonType]
    );

    const validateDynamicComparison = useCallback(
        async (_: any, propertyValue: string) => {
            if (!formSubmited) {
                return Promise.resolve();
            }
            if (propertyValue.startsWith("{{") && propertyValue.endsWith("}}")) {
                return Promise.resolve();
            }
            return Promise.reject(new Error("Invalid dynamic comparison format"));
        },
        [formSubmited]
    );
    useEffect(() => {
        if (!dateComparisonType && rootField) {
            const updatedFilters = [...formValues.filters];
            updatedFilters.splice(rootField.name, 1, {
                ...formValues.filters[rootField.name],
                dateComparisonType: "specific",
            });
            dispatch(
                setFields(
                    unflattenFields({
                        ...formValues,
                        filters: updatedFilters,
                    })
                )
            );
        }
    }, []);

    const onDataComparisonDateChangeDebounced = useMemo(
        () =>
            debounce((value: string) => {
                setDateTimeComparison(value);
                const currentPropertyValueType = form
                    ? getFilterFieldValue(form.getFieldsValue(), rootField.name, "propertyValueType")
                    : undefined;
                const commonPayload = {
                    dateComparisonType: value,
                    propertyValue: undefined,
                    relativeDateValue: undefined,
                    propertyValueType: currentPropertyValueType,
                };
                if (!rootField) {
                    dispatch(
                        setFields(
                            unflattenFields({
                                ...formValues,
                                ...commonPayload,
                            })
                        )
                    );
                    return;
                }
                const updatedFilters = [...formValues.filters];
                updatedFilters.splice(rootField.name, 1, {
                    ...formValues.filters[rootField.name],
                    ...commonPayload,
                });
                dispatch(
                    setFields(
                        unflattenFields({
                            ...formValues,
                            filters: updatedFilters,
                        })
                    )
                );
            }, 20),
        [dispatch, formValues, rootField, form]
    );

    function handleChange(propertyValue: string) {
        if (form) {
            const propertyValueType =
                getFilterFieldValue(form.getFieldsValue(), rootField.name, "propertyValueType") || "";

            let newPropValueType = null;
            if (propertyValueType != "variable" && propertyValue.startsWith("{{")) {
                newPropValueType = "variable";
            } else if (propertyValueType != "literal" && !propertyValue.startsWith("{{")) {
                newPropValueType = "literal";
            }
            // update propertyValueType if needed
            if (newPropValueType != null) {
                const formValuesCloned = cloneDeep(form.getFieldsValue());
                set(formValuesCloned, ["filters", rootField.name, "propertyValueType"], newPropValueType);
                form.setFieldsValue(formValuesCloned);
            }
        }
    }

    function getFilterFieldValue(formValues: any, filterIndex: number, fieldName: string) {
        const { filters } = formValues;
        const currentFilter = filters[filterIndex];
        return currentFilter[fieldName];
    }

    const getIconWithTooltip = () => {
        return (
            <Popover
                title={"Dynamic References"}
                placement="topRight"
                content={
                    <div className={styles.smallPopover}>
                        To dynamically reference property values from the triggering event, use&nbsp;
                        <span className={styles.exampleHandlebars}>&#123;&#123;handle bars&#125;&#125;</span>&nbsp;and
                        prepend property name with <b>event</b>. The available values for comparison are limited to the
                        property field type above.&nbsp;
                        <a
                            className="external-link"
                            target="_blank"
                            href="https://support.regal.ai/hc/en-us/articles/14157351216027-How-to-Use-Dynamic-Reference-in-Conditional-Node"
                            rel="noreferrer"
                        >
                            More Details
                        </a>
                    </div>
                }
            >
                <InfoCircleOutlined className={styles.relativeDatePopOverIcon} />
            </Popover>
        );
    };

    return (
        <>
            <RVFormFloatingLabel
                {...(rootField
                    ? { name: ["filters", rootField.name, "dateComparisonType"] }
                    : { name: "dateComparisonType" })}
            >
                <RVFormItem
                    {...(rootField
                        ? {
                              ...rootField,
                              name: [rootField.name, "dateComparisonType"],
                              dependencies: [[rootField.name, "propertyName"], "eventName"],
                          }
                        : { name: "dateComparisonType", dependencies: ["propertyName", "eventName"] })}
                    className={styles.formItem}
                    initialValue="specific"
                    label={rootField ? "Date Type" : undefined}
                >
                    <Select onChange={onDataComparisonDateChangeDebounced}>
                        <Select.Option value="specific">Specific Date (in Eastern Time Zone)</Select.Option>
                        <Select.Option value="relative">Relative Date</Select.Option>
                        <Select.Option value="dynamic-comparison">Dynamic Comparison</Select.Option>
                    </Select>
                </RVFormItem>
            </RVFormFloatingLabel>

            {dateTimeComparisonLocal == "specific" && (
                <RVFormFloatingLabel
                    {...(rootField
                        ? { name: ["filters", rootField.name, "propertyValue"] }
                        : { name: "propertyValue" })}
                >
                    <RVFormItem
                        {...(rootField
                            ? { ...rootField, name: [rootField.name, "propertyValue"] }
                            : { name: "propertyValue" })}
                        label={rootField ? "Date" : undefined}
                        rules={[
                            {
                                validator: validateSpecificDate,
                                message: "Select date",
                            },
                        ]}
                        className={styles.formItem}
                    >
                        <DatePickerItem placeholder={!rootField ? "Select Date" : undefined} />
                    </RVFormItem>
                </RVFormFloatingLabel>
            )}
            {dateTimeComparisonLocal == "relative" && (
                <RVFormFloatingLabel
                    {...(rootField
                        ? { name: ["filters", rootField.name, "relativeDateValue"] }
                        : { name: "relativeDateValue" })}
                >
                    <RVFormItem
                        {...(rootField
                            ? { ...rootField, name: [rootField.name, "relativeDateValue"] }
                            : { name: "relativeDateValue" })}
                        className={styles.midFormItem}
                        rules={[
                            {
                                validator: validateRelativeDate,
                                message: "Invalid relative date format",
                            },
                        ]}
                        label={rootField ? "Relative Date" : undefined}
                    >
                        <Input
                            placeholder={!rootField ? "now-5d-3h-15m" : undefined}
                            className={styles.formInput}
                            suffix={<RealtiveDatePopover />}
                        />
                    </RVFormItem>
                </RVFormFloatingLabel>
            )}
            {dateTimeComparisonLocal == "dynamic-comparison" && (
                <RVFormFloatingLabel
                    {...(rootField
                        ? { name: ["filters", rootField.name, "propertyValue"] }
                        : { name: "propertyValue" })}
                >
                    <RVFormItem
                        {...(rootField
                            ? {
                                  ...rootField,
                                  name: [rootField.name, "propertyValue"],
                                  dependencies: [[rootField.name, "propertyName"], "eventName"],
                              }
                            : { name: "propertyValue", dependencies: ["propertyName", "eventName"] })}
                        rules={[
                            {
                                validator: validateDynamicComparison,
                                message: "Invalid dynamic comparison format",
                            },
                            {
                                validator: propertyValueRequired,
                            },
                        ]}
                        label={rootField ? "Dynamic Comparison" : undefined}
                        className={styles.formItem}
                    >
                        <DynamicComparisonEditor
                            allOptions={allOptions}
                            loading={loading}
                            disabled={propertyValueDisabled}
                            onChange={handleChange}
                            rootField={rootField}
                            suffix={getIconWithTooltip()}
                            dataTestid={`property-select-${rootField?.name}-propertyValue-datetime`}
                        />
                    </RVFormItem>
                </RVFormFloatingLabel>
            )}
            <RVFormItem
                hidden={true}
                {...(rootField
                    ? {
                          ...rootField,
                          name: [rootField.name, "propertyValueType"],
                          dependencies: [[rootField.name, "propertyName"], "eventName"],
                      }
                    : { name: "propertyValueType", dependencies: ["propertyName", "eventName"] })}
            >
                <Input />
            </RVFormItem>
        </>
    );
}

function DynamicComparisonEditor({
    allOptions,
    loading,
    disabled,
    onChange,
    rootField,
    suffix,
    dataTestid,
}: {
    allOptions: Array<ConfigurableVariableOption>;
    loading: boolean;
    disabled: boolean;
    onChange: (value: string) => void;
    rootField?: any;
    suffix?: JSX.Element;
    dataTestid?: string;
}) {
    const form = Form.useFormInstance();
    const fieldPath = rootField ? ["filters", rootField.name, "propertyValue"] : ["propertyValue"];
    const initialValue = form.getFieldValue(fieldPath);

    const handleChange = useCallback(
        (newValue: string) => {
            const fieldObj = rootField
                ? { filters: { [rootField.name]: { propertyValue: newValue } } }
                : { propertyValue: newValue };
            form.setFieldsValue(fieldObj);
            onChange(newValue);
        },
        [form, onChange, rootField]
    );

    return (
        <div className={styles.dynamicComparisonEditor}>
            <PlainTextEditor
                className={styles.editorWrapper}
                inputClassName={styles.editorInput}
                data-testid={dataTestid}
                value={initialValue}
                onChange={handleChange}
                singleRow
                disabled={disabled}
            >
                <CustomVariablesHighlightPlugin prioritiziedRegexList={HANDLEBARS_REGEX_LIST} />
                <ConfigurableVariablesPickerPlugin
                    popAbove
                    fixedWidth
                    loading={loading}
                    options={allOptions}
                    prioritizedTriggers={HANDLEBAR_TRIGGERS}
                />
            </PlainTextEditor>
            {suffix}
        </div>
    );
}
